import { useAuth, useOrganization } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useSupabaseClient } from "@/hooks/use-supabase-client";
import type { Site, SiteStatus, SiteThemeConfig } from "@/types/database";

export interface SiteUpsert {
  status?: SiteStatus;
  seo_title?: string | null;
  seo_description?: string | null;
  theme_config?: SiteThemeConfig;
  calendar_visible_on_site?: boolean;
  accessibility_launcher_enabled?: boolean;
}

// The unit's one published site — auto-created on first load, matching the
// same "auto-provision on first authenticated read" pattern as
// use-org-profile.ts, since a Clerk organization existing is what makes a
// unit's site accessible in the first place; there's no separate "create a
// unit" step anymore now that identity is Clerk-org-only (Phase 2).
export function useSite() {
  const { organization } = useOrganization();
  const { isLoaded, isSignedIn } = useAuth();
  const getClient = useSupabaseClient();
  const queryClient = useQueryClient();
  const enabled = Boolean(organization) && isLoaded && isSignedIn;
  const queryKey = ["site", organization?.id] as const;

  const query = useQuery({
    queryKey,
    enabled,
    queryFn: async (): Promise<Site> => {
      if (!organization) throw new Error("No active organization");
      const supabase = await getClient();
      const { data, error } = await supabase
        .from("sites" as any)
        .select("*")
        .eq("clerk_org_id", organization.id)
        .maybeSingle();
      if (error) throw error;

      if (data) {
        const site = data as unknown as Site;

        // logo_url always tracks the org's own Clerk logo live — same
        // "snapshot on read, keep current" pattern as use-org-profile.ts's
        // unit_name, since the anonymous public site (PublicSite.tsx) has
        // no Clerk session to read organization.imageUrl directly. No
        // longer independently settable — see SiteUpsert, and the Logo
        // section of Site Styles, which is read-only for the same reason
        // Hub's Website > Style tab is. Tolerates a failed resync (e.g. a
        // view-only member without website_content:manage) by just
        // returning the not-yet-resynced row, matching unit_name's
        // handling below.
        if (site.logo_url !== organization.imageUrl) {
          const { data: resynced, error: resyncErr } = await supabase
            .from("sites" as any)
            .update({ logo_url: organization.imageUrl } as any)
            .eq("id", site.id)
            .select()
            .single();
          if (!resyncErr && resynced) return resynced as unknown as Site;
        }
        return site;
      }

      const { data: created, error: insertError } = await supabase
        .from("sites" as any)
        .insert({ clerk_org_id: organization.id, logo_url: organization.imageUrl } as any)
        .select()
        .single();
      if (insertError) throw insertError;
      return created as unknown as Site;
    },
  });

  const save = useMutation({
    mutationFn: async (updates: SiteUpsert): Promise<Site> => {
      const site = query.data;
      if (!site) throw new Error("No site loaded");
      const supabase = await getClient();
      const { data, error } = await supabase
        .from("sites" as any)
        .update(updates as any)
        .eq("id", site.id)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as Site;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    site: query.data ?? null,
    loading: query.isLoading,
    saving: save.isPending,
    error: query.error instanceof Error ? query.error.message : save.error instanceof Error ? save.error.message : null,
    save: (updates: SiteUpsert) => save.mutateAsync(updates),
  };
}
